import React, { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Alert,
  MenuItem,
  Typography,
} from "@mui/material";
import { Axios } from "../../../Api/axios";
import Cookie from "cookie-universal";
import { CLIENTS, PROJECTS, TAGS } from "../../../Api/Api";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TablePagination from "@mui/material/TablePagination";
import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import Grid from "@mui/material/Grid2";
import { useNavigate } from "react-router-dom";

const cookie = Cookie();
const token = cookie.get("dashboardLogin");
const userRole = cookie.get("userRole");

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#098040",
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

export default function ProjectsTable() {
  const [alert, setAlert] = useState(null);
  const [alertVisible, setAlertVisible] = useState(true);
  const [projects, setProjects] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [clients, setClients] = useState([]); // State to store clients
  const [tags, setTags] = useState([]); // State to store clients
  const [previewImage, setPreviewImage] = useState(null);
  const [images, setImages] = useState([]); // For multiple images
  const [backgroundImage, setBackgroundImage] = useState(null); // For background image
  const [existingImages, setExistingImages] = useState([]); // For existing images from the project

  const [selectedFile, setSelectedFile] = useState(null);
  const [editData, setEditData] = useState({
    id: null,
    title_en: "",
    title_ar: "",
    slug_en: "",
    slug_ar: "",
    desc_en: "",
    desc_ar: "",
    start_date: "",
    client_id: "",
    tag_id: "",
    url: "",
    image: null,
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("id");

  const navigate = useNavigate(); // Initialize the navigate function

  // Function to handle view button click
  const handleView = (projectId) => {
    navigate(`/panel/projects/${projectId}`); // Navigate to the project detail page
  };

  // Fetch existing images when editing a project
  useEffect(() => {
    if (isEditing && editData.images) {
      setExistingImages(editData.images); // Set existing images
    }
  }, [isEditing, editData]);

  // Handle multiple image uploads
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files); // Convert FileList to array
    setImages(files); // Update the images state
  };

  // Handle background image upload
  const handleBackgroundImageChange = (e) => {
    const file = e.target.files[0];
    setBackgroundImage(file); // Update the background image state
  };

  // Remove an image from the selected images
  const handleRemoveImage = (index) => {
    const updatedImages = images.filter((_, i) => i !== index);
    setImages(updatedImages);
  };

  // Remove the background image
  const handleRemoveBackgroundImage = () => {
    setBackgroundImage(null);
  };

  // Fetch clients when the component is mounted
  useEffect(() => {
    fetchProjects(); // Initial fetch on component mount

    // Fetch clients and tags (existing code)
    Axios.get(`/panel/${CLIENTS}`)
      .then((response) => {
        const clientOptions = response.data.map((client) => ({
          value: client.id,
          label: client.title_en,
        }));
        setClients(clientOptions);
      })
      .catch((error) => console.error("Error fetching clients:", error));

    Axios.get(`/panel/${TAGS}`)
      .then((response) => {
        const tagOptions = response.data.map((tag) => ({
          value: tag.id,
          label: tag.name_en,
        }));
        setTags(tagOptions);
      })
      .catch((error) => console.error("Error fetching Tags:", error));
  }, []); // Empty dependency array to run only once on mount

  const fetchProjects = async () => {
    try {
      const response = await Axios.get(`/panel/${PROJECTS}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-User-Role": userRole,
        },
      });
      setProjects(response.data);
    } catch (error) {
      console.error("Error fetching projects:", error);
      setAlert({
        message: "Error fetching projects",
        severity: "error",
      });
    }
  };

  const slugify = (text) => {
    return text
      .toString()
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-") // Replace spaces with -
      .replace(/[^a-z0-9\u0600-\u06FF\-]+/g, "") // Keep Arabic, English letters, numbers, and dashes
      .replace(/\-\-+/g, "-"); // Replace multiple - with single -
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    console.log("Name:", name, "Value:", value); // Debugging log

    setEditData((prev) => {
      const updatedData = {
        ...prev,
        [name]: value, // Update the field that changed
      };

      // Only update slugs if the title fields are being edited
      if (name === "title_en") {
        updatedData.slug_en = slugify(value);
      } else if (name === "title_ar") {
        updatedData.slug_ar = slugify(value);
      }

      console.log("Updated editData:", updatedData); // Debugging log
      return updatedData;
    });
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // const emptyRows =
  //   page > 0 ? Math.max(0, (1 + page) * rowsPerPage - projects.length) : 0;

  const visibleRows = React.useMemo(
    () =>
      [...projects]
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, projects]
  );

  const handleEdit = (id) => {
    const project = projects.find((row) => row.id === id);
    setEditData({
      id: project.id,
      title_en: project.title_en,
      title_ar: project.title_ar,
      slug_en: project.slug_en,
      slug_ar: project.slug_ar,
      desc_en: project.desc_en,
      desc_ar: project.desc_ar,
      start_date: project.start_date,
      client_id: project.client_id || "",
      tag_id: project.tag_id || "",
      url: project.url,
      image: project.image?.path || null,
    });
    setPreviewImage(project.image?.path || null);
    setIsEditing(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const preview = URL.createObjectURL(file);
      setPreviewImage(preview);
    }
  };

  const handleUpdate = async () => {
    if (!window.confirm("Are you sure you want to update this project?"))
      return;

    const formData = new FormData();
    formData.append("title_en", editData.title_en);
    formData.append("title_ar", editData.title_ar);
    formData.append("slug_en", editData.slug_en);
    formData.append("slug_ar", editData.slug_ar);
    formData.append("desc_en", editData.desc_en);
    formData.append("desc_ar", editData.desc_ar);
    formData.append("start_date", editData.start_date);
    formData.append("client_id", editData.client_id);
    formData.append("tag_id", editData.tag_id);
    formData.append("url", editData.url);
    formData.append("_method", "PUT");

    // Append multiple images
    images.forEach((image) => {
      formData.append("images[]", image); // Use "images[]" for multiple files
    });

    // Append background image
    if (backgroundImage) {
      formData.append("background_image", backgroundImage);
    }

    try {
      await Axios.post(`/panel/${PROJECTS}/${editData.id}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-User-Role": userRole,
          "Content-Type": "multipart/form-data",
        },
      });

      await fetchProjects();
      setAlert({
        message: "Project updated successfully",
        severity: "success",
      });
      handleCloseEdit();
    } catch (error) {
      console.error("Error updating Project:", error);
      setAlert({ message: "Error updating Project", severity: "error" });
    }

    setTimeout(() => setAlertVisible(false), 2000);
    setAlertVisible(true);
  };

  const handleCloseEdit = () => {
    setSelectedFile(null);
    setPreviewImage(null);
    setIsEditing(false);
    setEditData({
      id: null,
      title_en: "",
      title_ar: "",
      slug_en: "",
      slug_ar: "",
      desc_en: "",
      desc_ar: "",
      start_date: "",
      url: "",
      image: null,
    });
    if (previewImage) URL.revokeObjectURL(previewImage);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this project?"))
      return;

    try {
      await Axios.delete(`/panel/${PROJECTS}/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-User-Role": userRole,
        },
      });

      setProjects(projects.filter((project) => project.id !== id));
      setAlert({
        message: "Project deleted successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Error deleting Project:", error);
      setAlert({ message: "Error deleting Project", severity: "error" });
    }

    setTimeout(() => setAlertVisible(false), 2000);
    setAlertVisible(true);
  };

  return (
    <Box sx={{ height: "100%", width: "100%" }}>
      {alert && alertVisible && (
        <Alert
          severity={alert.severity}
          onClose={() => setAlertVisible(false)}
          sx={{
            transition: "opacity 1s ease-out",
            opacity: alertVisible ? 1 : 0,
          }}
        >
          {alert.message}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 700 }} aria-label="customized table">
          <TableHead>
            <TableRow>
              <StyledTableCell
                onClick={(e) => handleRequestSort(e, "id")}
                style={{ cursor: "pointer" }}
              >
                ID
                {orderBy === "id" ? (
                  order === "desc" ? (
                    <ArrowDownward />
                  ) : (
                    <ArrowUpward />
                  )
                ) : orderBy === "id" && order === "asc" ? (
                  <ArrowUpward />
                ) : (
                  <ArrowDownward />
                )}
              </StyledTableCell>
              <StyledTableCell
                onClick={(e) => handleRequestSort(e, "title_en")}
                style={{ cursor: "pointer" }}
              >
                Title (English)
                {orderBy === "title_en" ? (
                  order === "desc" ? (
                    <ArrowDownward />
                  ) : (
                    <ArrowUpward />
                  )
                ) : orderBy === "title_en" && order === "asc" ? (
                  <ArrowUpward />
                ) : (
                  <ArrowDownward />
                )}
              </StyledTableCell>
              <StyledTableCell
                onClick={(e) => handleRequestSort(e, "title_ar")}
                style={{ cursor: "pointer" }}
              >
                العنوان
                {orderBy === "title_ar" ? (
                  order === "desc" ? (
                    <ArrowDownward />
                  ) : (
                    <ArrowUpward />
                  )
                ) : orderBy === "title_ar" && order === "asc" ? (
                  <ArrowUpward />
                ) : (
                  <ArrowDownward />
                )}
              </StyledTableCell>
              <StyledTableCell
                onClick={(e) => handleRequestSort(e, "slug_en")}
                style={{ cursor: "pointer" }}
              >
                Slug
                {orderBy === "slug_en" ? (
                  order === "desc" ? (
                    <ArrowDownward />
                  ) : (
                    <ArrowUpward />
                  )
                ) : orderBy === "slug_en" && order === "asc" ? (
                  <ArrowUpward />
                ) : (
                  <ArrowDownward />
                )}
              </StyledTableCell>
              <StyledTableCell
                onClick={(e) => handleRequestSort(e, "slug_ar")}
                style={{ cursor: "pointer" }}
              >
                Slug Ar
                {orderBy === "slug_ar" ? (
                  order === "desc" ? (
                    <ArrowDownward />
                  ) : (
                    <ArrowUpward />
                  )
                ) : orderBy === "slug_ar" && order === "asc" ? (
                  <ArrowUpward />
                ) : (
                  <ArrowDownward />
                )}
              </StyledTableCell>
              <StyledTableCell
                onClick={(e) => handleRequestSort(e, "desc_en")}
                style={{ cursor: "pointer" }}
              >
                Description
                {orderBy === "desc_en" ? (
                  order === "desc" ? (
                    <ArrowDownward />
                  ) : (
                    <ArrowUpward />
                  )
                ) : orderBy === "desc_en" && order === "asc" ? (
                  <ArrowUpward />
                ) : (
                  <ArrowDownward />
                )}
              </StyledTableCell>
              <StyledTableCell
                onClick={(e) => handleRequestSort(e, "desc_ar")}
                style={{ cursor: "pointer" }}
              >
                التفاصيل
                {orderBy === "desc_ar" ? (
                  order === "desc" ? (
                    <ArrowDownward />
                  ) : (
                    <ArrowUpward />
                  )
                ) : orderBy === "desc_ar" && order === "asc" ? (
                  <ArrowUpward />
                ) : (
                  <ArrowDownward />
                )}
              </StyledTableCell>
              <StyledTableCell
                onClick={(e) => handleRequestSort(e, "client.title_en")}
                style={{ cursor: "pointer" }}
              >
                Client
                {orderBy === "client.title_en" ? (
                  order === "desc" ? (
                    <ArrowDownward />
                  ) : (
                    <ArrowUpward />
                  )
                ) : orderBy === "client.title_en" && order === "asc" ? (
                  <ArrowUpward />
                ) : (
                  <ArrowDownward />
                )}
              </StyledTableCell>
              <StyledTableCell
                onClick={(e) => handleRequestSort(e, "tag.name_en")}
                style={{ cursor: "pointer" }}
              >
                Tag
                {orderBy === "tag.name_en" ? (
                  order === "desc" ? (
                    <ArrowDownward />
                  ) : (
                    <ArrowUpward />
                  )
                ) : orderBy === "tag.name_en" && order === "asc" ? (
                  <ArrowUpward />
                ) : (
                  <ArrowDownward />
                )}
              </StyledTableCell>
              <StyledTableCell
                onClick={(e) => handleRequestSort(e, "start_date")}
                style={{ cursor: "pointer" }}
              >
                Start Date
                {orderBy === "start_date" ? (
                  order === "desc" ? (
                    <ArrowDownward />
                  ) : (
                    <ArrowUpward />
                  )
                ) : orderBy === "start_date" && order === "asc" ? (
                  <ArrowUpward />
                ) : (
                  <ArrowDownward />
                )}
              </StyledTableCell>
              <StyledTableCell>Image</StyledTableCell>
              <StyledTableCell>Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((project) => {
              const backgroundImage = project.images?.find(
                (img) => img.is_background
              )?.path;

              return (
                <StyledTableRow key={project.id}>
                  <StyledTableCell>{project.id}</StyledTableCell>
                  <StyledTableCell>{project.title_en}</StyledTableCell>
                  <StyledTableCell>{project.title_ar}</StyledTableCell>
                  <StyledTableCell>{project.slug_en}</StyledTableCell>
                  <StyledTableCell>{project.slug_ar}</StyledTableCell>
                  <StyledTableCell>{project.desc_en}</StyledTableCell>
                  <StyledTableCell>{project.desc_ar}</StyledTableCell>
                  <StyledTableCell>
                    {project.client?.title_en || "N/A"}
                  </StyledTableCell>
                  <StyledTableCell>
                    {project.tag?.name_en || "N/A"}
                  </StyledTableCell>
                  <StyledTableCell>{project.start_date}</StyledTableCell>

                  <StyledTableCell>
                    {backgroundImage ? (
                      <img
                        src={backgroundImage}
                        alt="project"
                        style={{
                          width: 50,
                          height: 50,
                        }}
                        onError={(e) => {
                          e.target.src = "/placeholder-image.png"; // Fallback image
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: 50,
                          height: 50,
                          backgroundColor: "#f0f0f0",
                          borderRadius: 4,
                        }}
                      />
                    )}
                  </StyledTableCell>
                  <StyledTableCell>
                    <IconButton
                      onClick={() => handleEdit(project.id)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDelete(project.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleView(project.id)} // Pass the project ID
                      color="secondary"
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </StyledTableCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={projects.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <Dialog open={isEditing} onClose={handleCloseEdit}>
        <DialogTitle>Edit Project</DialogTitle>
        <DialogContent>
          <TextField
            label="Title (English)"
            name="title_en" // Ensure this matches the state key
            fullWidth
            value={editData.title_en} // Bind to the state
            onChange={handleInputChange} // Ensure this is correctly set
            sx={{ mb: 2, mt: 2 }}
          />
          <TextField
            label="Title (Arabic)"
            name="title_ar" // Ensure this matches the state key
            fullWidth
            value={editData.title_ar} // Bind to the state
            onChange={handleInputChange} // Ensure this is correctly set
            sx={{ mb: 2 }}
          />
          <TextField
            label="Slug (English)"
            name="slug_en"
            value={editData.slug_en}
            fullWidth
            sx={{ mb: 2 }}
            margin="dense"
            disabled
          />
          <TextField
            label="Slug (Ar)"
            name="slug_ar"
            value={editData.slug_ar}
            sx={{ mb: 2 }}
            fullWidth
            margin="dense"
            disabled
          />
          <TextField
            label="Description"
            fullWidth
            value={editData.desc_en}
            onChange={(e) =>
              setEditData({ ...editData, desc_en: e.target.value })
            }
            sx={{ mb: 2 }}
          />
          <TextField
            label="التفاصيل"
            fullWidth
            value={editData.desc_ar}
            onChange={(e) =>
              setEditData({ ...editData, desc_ar: e.target.value })
            }
            sx={{ mb: 2 }}
          />
          <TextField
            id="outlined-select-client"
            fullWidth
            sx={{ mb: 2 }}
            select
            label="Client"
            value={editData.client_id}
            onChange={(e) =>
              setEditData({ ...editData, client_id: e.target.value })
            }
          >
            {clients.map((client) => (
              <MenuItem key={client.value} value={client.value.toString()}>
                {/* Ensure string value */}
                {client.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            id="outlined-select-tag"
            margin="dense"
            label="Tag"
            name="tag"
            select
            value={editData.tag_id}
            onChange={(e) =>
              setEditData({ ...editData, tag_id: e.target.value })
            }
            sx={{ mb: 2 }}
            fullWidth
          >
            {tags.map((tag) => (
              <MenuItem key={tag.value} value={tag.value.toString()}>
                {/* Ensure string value */}
                {tag.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="URL"
            name="url"
            fullWidth
            value={editData.url}
            onChange={(e) => setEditData({ ...editData, url: e.target.value })}
            sx={{ mb: 2 }}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              sx={{ width: "100%" }}
              margin="normal"
              className="mb-3 mt-3"
              label="Start Date"
              value={editData.start_date ? dayjs(editData.start_date) : null} // Ensure it's a dayjs object
              onChange={
                (date) =>
                  setEditData({
                    ...editData,
                    start_date: date ? date.format("YYYY-MM-DD") : null,
                  }) // Convert to string format
              }
            />
          </LocalizationProvider>
          {/* Upload Multiple Images */}
          <Box
            sx={{
              border: "2px dashed #1976d2",
              borderRadius: 2,
              p: 3,
              textAlign: "center",
              cursor: "pointer",
              position: "relative",
              bgcolor: "#f0f8ff",
              "&:hover": { bgcolor: "#e3f2fd" },
              mt: 2,
            }}
          >
            <input
              type="file"
              accept="image/*"
              multiple
              style={{
                opacity: 0,
                width: "100%",
                height: "100%",
                position: "absolute",
                top: 0,
                left: 0,
                cursor: "pointer",
              }}
              onChange={handleImageChange}
            />
            <CloudUploadIcon sx={{ fontSize: 50, color: "#1976d2" }} />
            <Typography variant="body1" color="primary">
              Click or Drag to Upload Images
            </Typography>
          </Box>

          {/* Show existing images */}
          {existingImages.length > 0 && (
            <Box mt={2}>
              <Typography variant="h6">Existing Images</Typography>
              <Grid container spacing={2}>
                {existingImages.map((image, index) => (
                  <Grid item xs={4} key={index}>
                    <Box textAlign="center">
                      <img
                        src={image.path} // Use the image path from the server
                        alt={`Existing Image ${index + 1}`}
                        style={{
                          width: "100%",
                          height: "auto",
                          borderRadius: 8,
                          marginTop: 10,
                        }}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Show selected images preview */}
          {images.length > 0 && (
            <Box mt={2}>
              <Typography variant="h6">New Images</Typography>
              <Grid container spacing={2}>
                {images.map((image, index) => (
                  <Grid item xs={4} key={index}>
                    <Box textAlign="center">
                      <Typography variant="body2">{image.name}</Typography>
                      <img
                        src={URL.createObjectURL(image)}
                        alt="Preview"
                        style={{
                          width: "100%",
                          height: "auto",
                          borderRadius: 8,
                          marginTop: 10,
                        }}
                      />
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleRemoveImage(index)}
                      >
                        Remove
                      </Button>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Upload Background Image */}
          <Box
            sx={{
              border: "2px dashed #1976d2",
              borderRadius: 2,
              p: 3,
              textAlign: "center",
              cursor: "pointer",
              position: "relative",
              bgcolor: "#f0f8ff",
              "&:hover": { bgcolor: "#e3f2fd" },
              mt: 2,
            }}
          >
            <input
              type="file"
              accept="image/*"
              style={{
                opacity: 0,
                width: "100%",
                height: "100%",
                position: "absolute",
                top: 0,
                left: 0,
                cursor: "pointer",
              }}
              onChange={handleBackgroundImageChange}
            />
            <CloudUploadIcon sx={{ fontSize: 50, color: "#1976d2" }} />
            <Typography variant="body1" color="primary">
              Click or Drag to Upload Background Image
            </Typography>
          </Box>

          {/* Show selected background image preview */}
          {backgroundImage && (
            <Box mt={2} textAlign="center">
              <Typography variant="body2">{backgroundImage.name}</Typography>
              <img
                src={URL.createObjectURL(backgroundImage)}
                alt="Background Preview"
                style={{
                  width: "20%",
                  height: "auto",
                  borderRadius: 8,
                  marginTop: 10,
                }}
              />
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={handleRemoveBackgroundImage}
              >
                Remove
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit} color="primary">
            Cancel
          </Button>
          <Button onClick={handleUpdate} color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}
